import { useState } from 'react';
import { router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card } from '../../../components/ui';
import { Section } from '../../../components/layout/AppShell';
import { cn } from '../../../lib/cn';

const REASON_LABELS = {
  context_missing: 'Context missing',
  definition_circular: 'Circular definition',
  options_ambiguous: 'Ambiguous options',
  word_doesnt_fit: "Word doesn't fit",
  spelling_issue: 'Spelling issue',
  other: 'Other',
};

const STATUS_COLORS = {
  open: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  resolved: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  dismissed: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
};

export default function VocabularyFlagsIndex({ flags, open_count }) {
  const [filter, setFilter] = useState('open');

  const visible = filter === 'all' ? flags : flags.filter((f) => f.status === filter);

  return (
    <TeacherShell scrollTitle="Vocab Flags">
      <TeacherPageHeader
        title="Vocabulary Flags"
        subtitle={`${open_count} open flag${open_count === 1 ? '' : 's'} awaiting review`}
      />

      <div className="flex gap-2 mb-6">
        {['open', 'dismissed', 'resolved', 'all'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            className={cn(
              'text-xs font-medium px-3 py-1.5 rounded-full border transition-colors capitalize',
              filter === s
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:border-gray-500'
            )}
          >
            {s}
          </button>
        ))}
      </div>

      <Section>
        {visible.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            No flags with status "{filter}".
          </div>
        ) : (
          <Card padding="none">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Word</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reason</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stage</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Student</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Age</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {visible.map((flag) => (
                    <FlagRow key={flag.id} flag={flag} />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </Section>
    </TeacherShell>
  );
}

function FlagRow({ flag }) {
  const [busy, setBusy] = useState(null);

  const act = (action) => {
    setBusy(action);
    router.patch(`/admin/vocabulary_flags/${flag.id}/${action}`, {}, {
      onFinish: () => setBusy(null),
    });
  };

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors">
      <td className="px-4 py-3">
        {flag.word ? (
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {flag.word.lemma}
            {flag.word.pos && (
              <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">{flag.word.pos}</span>
            )}
          </span>
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <p className="text-sm text-gray-700 dark:text-gray-300">{REASON_LABELS[flag.reason] ?? flag.reason}</p>
        {flag.reason_text && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-xs truncate" title={flag.reason_text}>
            "{flag.reason_text}"
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {flag.stage_or_format ? (
          <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{flag.stage_or_format}</span>
        ) : (
          <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
        {flag.user?.full_name ?? '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
        {flag.age_in_days === 0 ? 'today' : `${flag.age_in_days}d ago`}
      </td>
      <td className="px-4 py-3">
        <span className={cn('text-xs font-medium px-2 py-0.5 rounded capitalize', STATUS_COLORS[flag.status])}>
          {flag.status}
        </span>
        {flag.resolved_by && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">by {flag.resolved_by.full_name}</p>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        {flag.status === 'open' && (
          <div className="flex justify-end gap-2">
            <ActionButton
              label="Resolve"
              onClick={() => act('resolve')}
              busy={busy === 'resolve'}
              variant="green"
            />
            <ActionButton
              label="Dismiss"
              onClick={() => act('dismiss')}
              busy={busy === 'dismiss'}
              variant="gray"
            />
          </div>
        )}
      </td>
    </tr>
  );
}

function ActionButton({ label, onClick, busy, variant }) {
  const colors = {
    green: 'text-green-700 hover:bg-green-50 border-green-200 dark:text-green-400 dark:hover:bg-green-950/30 dark:border-green-800',
    gray: 'text-gray-500 hover:bg-gray-50 border-gray-200 dark:text-gray-400 dark:hover:bg-gray-800 dark:border-gray-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={cn(
        'text-xs font-medium px-2.5 py-1 rounded border transition-colors',
        colors[variant],
        busy && 'opacity-50 cursor-not-allowed'
      )}
    >
      {busy ? '…' : label}
    </button>
  );
}
